// src/components/GlobalStyles.js
import { createGlobalStyle } from "styled-components";
import theme from "../styles/theme";

const GlobalStyles = createGlobalStyle`
  @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800&family=Playfair+Display:wght@400;500;600;700&display=swap');
  
  * {
    margin: 0;
    padding: 0;
    box-sizing: border-box;
  }

  body {
    font-family: ${theme.typography.fontFamily.primary};
    line-height: ${theme.typography.lineHeight.normal};
    color: ${theme.secondary[800]};
    background-color: #ffffff;
    overflow-x: hidden;
    font-weight: ${theme.typography.fontWeight.normal};
    margin: 0;
    padding: 0;
  }

  main {
    margin: 0;
    padding: 0;
  }

  /* Ensure no gap between header and first section */
  main > section:first-child {
    margin-top: 0;
    padding-top: 0;
  }

  h1, h2, h3, h4, h5, h6 {
    font-family: ${theme.typography.fontFamily.heading};
    font-weight: ${theme.typography.fontWeight.semibold};
    line-height: ${theme.typography.lineHeight.tight};
    color: ${theme.secondary[900]};
  }

  h1 {
    font-size: ${theme.typography.fontSize['5xl']};
    font-weight: ${theme.typography.fontWeight.bold};
    
    @media (max-width: ${theme.breakpoints.md}) {
      font-size: ${theme.typography.fontSize['4xl']};
    }
  }

  h2 {
    font-size: ${theme.typography.fontSize['4xl']};
    
    @media (max-width: ${theme.breakpoints.md}) {
      font-size: ${theme.typography.fontSize['3xl']};
    }
  }

  h3 {
    font-size: ${theme.typography.fontSize['2xl']};
  }

  h4 {
    font-size: ${theme.typography.fontSize.xl};
  }

  p {
    color: ${theme.secondary[600]};
    font-weight: ${theme.typography.fontWeight.normal};
    line-height: ${theme.typography.lineHeight.relaxed};
  }

  a {
    text-decoration: none;
    color: inherit;
    transition: all ${theme.animation.normal} ease;
  }

  button {
    font-family: ${theme.typography.fontFamily.primary};
    cursor: pointer;
    border: none;
    outline: none;
    transition: all ${theme.animation.normal} ease;
  }

  img {
    max-width: 100%;
    height: auto;
  }

  /* Custom scrollbar */
  ::-webkit-scrollbar {
    width: 8px;
  }

  ::-webkit-scrollbar-track {
    background: ${theme.secondary[100]};
  }

  ::-webkit-scrollbar-thumb {
    background: ${theme.secondary[300]};
    border-radius: ${theme.borderRadius.base};
  }

  ::-webkit-scrollbar-thumb:hover {
    background: ${theme.secondary[400]};
  }

  /* Smooth scrolling */
  html {
    scroll-behavior: smooth;
  }

  /* Selection styles */
  ::selection {
    background: ${theme.primary[600]};
    color: white;
  }

  /* Focus styles for accessibility */
  *:focus {
    outline: 2px solid ${theme.primary[500]};
    outline-offset: 2px;
  }

  /* Container max-width */
  .container {
    max-width: 1200px;
    margin: 0 auto;
    padding: 0 ${theme.spacing[6]};
    
    @media (max-width: ${theme.breakpoints.sm}) {
      padding: 0 ${theme.spacing[4]};
    }
  }

  /* Utility classes */
  .text-gradient-primary {
    background: ${theme.gradients.primary};
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }

  .text-gradient-accent {
    background: ${theme.gradients.accent};
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }

  .glass-effect {
    background: ${theme.glass.background};
    backdrop-filter: ${theme.glass.backdropFilter};
    border: ${theme.glass.border};
  }

  .glass-effect-strong {
    background: ${theme.glass.backgroundStrong};
    backdrop-filter: ${theme.glass.backdropFilterStrong};
    border: ${theme.glass.border};
  }

  .shadow-soft {
    box-shadow: ${theme.shadows.md};
  }

  .shadow-medium {
    box-shadow: ${theme.shadows.lg};
  }

  .shadow-large {
    box-shadow: ${theme.shadows.xl};
  }

  .shadow-2xl {
    box-shadow: ${theme.shadows['2xl']};
  }

  /* Professional button styles */
  .btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: ${theme.spacing[2]};
    padding: ${theme.spacing[3]} ${theme.spacing[6]};
    font-weight: ${theme.typography.fontWeight.semibold};
    border-radius: ${theme.borderRadius.full};
    transition: all ${theme.animation.normal} ease;
    text-decoration: none;
    cursor: pointer;
    border: none;
    font-size: ${theme.typography.fontSize.base};
  }

  .btn-primary {
    background: ${theme.gradients.primary};
    color: white;
    box-shadow: 0 4px 14px rgba(59, 130, 246, 0.3);
    
    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 8px 25px rgba(59, 130, 246, 0.4);
    }

    &:active {
      transform: translateY(0);
    }
  }

  .btn-secondary {
    background: ${theme.glass.background};
    backdrop-filter: ${theme.glass.backdropFilter};
    border: ${theme.glass.border};
    color: ${theme.secondary[700]};
    
    &:hover {
      background: ${theme.glass.backgroundStrong};
      transform: translateY(-2px);
    }
  }

  .btn-accent {
    background: ${theme.gradients.accent};
    color: white;
    box-shadow: 0 4px 14px rgba(16, 185, 129, 0.3);
    
    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 8px 25px rgba(16, 185, 129, 0.4);
    }
  }

  /* Responsive typography */
  @media (max-width: ${theme.breakpoints.sm}) {
    body {
      font-size: ${theme.typography.fontSize.sm};
    }
  }

  /* Professional animations */
  @keyframes fadeInUp {
    from {
      opacity: 0;
      transform: translateY(30px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }

  @keyframes slideInLeft {
    from {
      opacity: 0;
      transform: translateX(-30px);
    }
    to {
      opacity: 1;
      transform: translateX(0);
    }
  }

  @keyframes slideInRight {
    from {
      opacity: 0;
      transform: translateX(30px);
    }
    to {
      opacity: 1;
      transform: translateX(0);
    }
  }

  @keyframes float {
    0%, 100% {
      transform: translateY(0px);
    }
    50% {
      transform: translateY(-20px);
    }
  }

  /* Animation utility classes */
  .animate-fadeInUp {
    animation: fadeInUp 0.6s ease-out;
  }

  .animate-fadeIn {
    animation: fadeIn 0.6s ease-out;
  }

  .animate-float {
    animation: float 6s ease-in-out infinite;
  }

  /* International accessibility */
  [lang="ar"], [lang="he"] {
    direction: rtl;
  }

  /* Print styles */
  @media print {
    .no-print {
      display: none !important;
    }
    
    body {
      background: white !important;
      color: black !important;
    }
  }
`;

export default GlobalStyles;
